<template>
  <div class="app-container">
    <!-- 测试使用过的代码，可以保留后续验证时候使用 可以动态全局切换主题色 -->
    <!-- <div @click="changeColor">切换颜色</div> -->
    <d3_toast />
    <router-view v-if="showRouterView" />
    <SwipeBack />
    <!-- 全局广告覆盖 -->
    <AdPage v-if="detectPlatform() != 1" />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, defineAsyncComponent, watch, ref, computed } from 'vue';
import AdPage from '@/layout/services/AdPage.vue';
import { Keyboard } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';
import { statusBarAdapter } from '@/capacitor/statusbar/statusbaradapter';
import d3_toast from 'd3components/src/components/item-d3/third/d3_toast.vue';
import { useSettingStore } from 'd3components';
import { adService } from '@/layout/services/AdService';

import { detectPlatform } from './utils/platform'
const SwipeBack = defineAsyncComponent(() => import('@/capacitor/exited/swipeback.vue'));
const settingStore = useSettingStore();
const showRouterView = computed(() => !adService.startupBlocking.value);
// - 使用自身主题变量体系（public/varlet-themes/*.css + src/assets/app.css）
// - 暗黑模式通过设置 html[theme-mode='dark'] 生效（变量名：--color-* / --hsl-*）
// - DevTools 里会看到 :root[theme-mode='dark'] 下出现大量 --td-* 变量（TDesign 主题变量）
// 原因：
// - d3components 内包含 web-tdesign 相关组件/样式，它可能通过 <style> 或 <link> 运行时注入
//   一份包含 --td-* 的主题变量。
// - 当组件样式使用的是 var(--td-xxx) 时，就会被这套变量驱动；并且注入顺序靠后时，
//   还可能在调试上造成“暗黑不生效/被覆盖”的错觉。
// - 之前的逻辑（settingStore.updateConfig）先不删除，避免影响 d3components 的配置更新链路。
// - 在不破坏既有链路的前提下，增加“兜底/护栏”逻辑，尽量让最终生效的主题样式回到自己的 varlet-themes 变量体系。

// ：清理 TDesign 主题注入
// - 删除 <style> 中包含 --td- 的注入片段
// - 删除 href 指向 tdesign/web-tdesign 的样式 link
// 如果某些组件强依赖 --td-* 变量，清理后可能出现样式退化；这类组件应逐步迁移到
// 使用项目统一变量（--color-*）。
const purgeTDesignTheme = () => {
  const styles = Array.from(document.querySelectorAll('style'));
  for (const el of styles) {
    const txt = el.textContent || '';
    if (txt.includes('--td-')) el.remove();
  }

  const links = Array.from(document.querySelectorAll('link[rel="stylesheet"]')) as HTMLLinkElement[];
  for (const el of links) {
    const href = (el.getAttribute('href') || '').toLowerCase();
    if (!href) continue;
    if (href.includes('tdesign') || href.includes('t-design') || href.includes('web-tdesign')) el.remove();
  }
};

const applyDisplayMode = (mode: string) => {
  if (mode === 'dark') {
    document.documentElement.setAttribute('theme-mode', 'dark');
  } else {
    document.documentElement.setAttribute('theme-mode', '');
  }

  // 主题切换后立刻清理可能被注入的 TDesign 主题变量，避免影响“项目自有变量体系”的判定与显示。
  purgeTDesignTheme();

  // 主题切换时同步更新原生状态栏样式（避免浅色背景下仍是白字导致不可见）
  if (Capacitor.isNativePlatform()) {
    statusBarAdapter.updateStyleForCurrentPage().catch((e) => {
      console.warn('主题切换后更新状态栏失败:', e);
    });
  }
};



let showTimer: ReturnType<typeof setTimeout> | null = null;
let hideTimer: ReturnType<typeof setTimeout> | null = null;



// 全局键盘监听，确保底部导航栏在键盘弹起时保持固定
onMounted(() => {
  // const themeMap = {
  //   tencent: '214',
  //   blue: '114'
  // }

  // //初始化颜色
  // document.documentElement.style.setProperty(
  //   '--D3-primary-h',
  //   themeMap[(settingStore as any).brandTheme]
  // )


  // 之前的逻辑：初始化一次配置（可能触发 d3components 内部的主题/配置同步）
  // 保留原因：避免破坏 d3components 的既有配置更新链路。
  // 风险：库侧可能在此过程中注入 TDesign 主题变量（--td-*）。因此下面会进行 applyBrandTheme + purge。
  if (typeof (settingStore as any).updateConfig === 'function') {
    (settingStore as any).updateConfig({});
  }



  // 清理库侧可能注入的 TDesign 主题变量（--td-*）
  purgeTDesignTheme();

  applyDisplayMode(String((settingStore as any).displayMode || 'light'));

  watch(
    () => (settingStore as any).displayMode,
    (val) => applyDisplayMode(String(val || 'light'))
  );

  watch(
    () => (settingStore as any).brandTheme,
    () => {
      // 品牌主题色变化时：
      // 1) 重新指向项目主题文件（避免被库侧 link 注入覆盖）
      // 2) 清理库侧可能再次注入的 TDesign 主题变量（--td-*）
      purgeTDesignTheme();
    }
  );

  if (Capacitor.isNativePlatform()) {
    Keyboard.addListener('keyboardWillShow', () => {
      // 如果showTimer存在，清除showTimer
      if (showTimer) clearTimeout(showTimer);
      // 添加键盘打开的类
      document.body.classList.add('keyboard-open');
    });

    Keyboard.addListener('keyboardWillHide', () => {
      if (hideTimer) clearTimeout(hideTimer);
      // 延迟恢复，避免闪烁
      hideTimer = setTimeout(() => {
        // 移除键盘打开的类
        document.body.classList.remove('keyboard-open');
      }, 250);
    });
  }
});
// 卸载时清除键盘监听
onBeforeUnmount(() => {
  // 如果showTimer存在，清除showTimer 
  if (showTimer) clearTimeout(showTimer);
  // 如果hideTimer存在，清除hideTimer
  if (hideTimer) clearTimeout(hideTimer);
  // 如果Capacitor.isNativePlatform()为true，清除键盘监听
  if (Capacitor.isNativePlatform()) Keyboard.removeAllListeners();
});
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--color-text);
  width: 100%;
  overflow: hidden;

}

.app-container {
  width: 100%;
  height: 100vh;
  box-sizing: border-box;
  background-color: var(--color-body);
  overflow: hidden;
}

/* 状态栏适配相关样式类 - 条件性应用 */
/* 默认情况下这些类不应用任何样式，只有在需要适配的设备上才生效 */

/* 只有在需要适配的设备上才应用这些样式 */
.needs-status-bar-adaptation .status-bar-safe {
  padding-top: var(--safe-area-inset-top) !important;
}

.needs-status-bar-adaptation .content-safe-area {
  padding-top: var(--content-safe-padding) !important;
  padding-left: var(--safe-area-inset-left) !important;
  padding-right: var(--safe-area-inset-right) !important;
}

.needs-status-bar-adaptation .full-safe-area {
  padding-top: var(--safe-area-inset-top) !important;
  padding-bottom: var(--safe-area-inset-bottom) !important;
  padding-left: var(--safe-area-inset-left) !important;
  padding-right: var(--safe-area-inset-right) !important;
}
</style>
